import React, { useMemo, useState } from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/components/ui/use-toast';

import FileGraph, { type GraphScope } from '@/components/ruvector/FileGraph';
import { isRuVectorAvailable } from '@/lib/ruvectorClient';
import { enableAgentGuard, disableAgentGuard, isAgentGuardEnabled, clearAllowlistPrefixes, getAllowlistPrefixes } from '@/lib/agentAccess';

import { Laptop, Shield } from 'lucide-react';

export default function IntelligenceXP() {

  const { toast } = useToast();

  const [scope, setScope] = useState<GraphScope>('documents');
  const [showContainment, setShowContainment] = useState(true);
  const [showSemantic, setShowSemantic] = useState(true);
  const [showActivity, setShowActivity] = useState(true);

  const [semanticThreshold, setSemanticThreshold] = useState(0.75);
  const [topN, setTopN] = useState(3);

  const [buildSignal, setBuildSignal] = useState(0);
  const [clearSignal, setClearSignal] = useState(0);

  const localSearchAvailable = useMemo(() => isRuVectorAvailable(), []);

  const allowlistSummary = useMemo(() => {
    const p = getAllowlistPrefixes();
    if (p.length === 0) return 'No allowlist set';
    if (p.length === 1) return p[0];
    return `${p.length} prefixes`;
  }, [buildSignal, clearSignal]);

  const guardEnabled = isAgentGuardEnabled();

  return (
    <MainLayout>
      <div className="container px-4 py-6 max-w-7xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">IntelligenceXP</h1>

            <p className="text-sm text-muted-foreground mt-1">
              System File Graph + controlled agent allowlist (v1).
            </p>
          </div>
          <Badge variant={localSearchAvailable ? 'default' : 'secondary'} className="flex items-center gap-2">
            <Laptop className="h-4 w-4" />
            {localSearchAvailable ? 'Local search enabled' : 'Web-only build'}
          </Badge>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>System File Graph</CardTitle>
            <CardDescription>Build and filter the graph. Local semantic edges are currently unavailable in the web-only build.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Scope</Label>
                <Select value={scope} onValueChange={(v) => setScope(v as GraphScope)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select scope" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="documents">Documents</SelectItem>
                    <SelectItem value="desktop">Desktop</SelectItem>
                    <SelectItem value="projects">Projects</SelectItem>
                    <SelectItem value="chats">Chats</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="block">Edges</Label>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Folder edges</span>
                  <Switch checked={showContainment} onCheckedChange={setShowContainment} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Semantic edges</span>
                  <Switch checked={showSemantic} onCheckedChange={setShowSemantic} disabled={!localSearchAvailable} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Activity edges</span>
                  <Switch checked={showActivity} onCheckedChange={setShowActivity} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Semantic threshold ({semanticThreshold.toFixed(2)})</Label>
                <Slider
                  value={[semanticThreshold]}
                  min={0.6}
                  max={0.9}
                  step={0.01}
                  onValueChange={(v) => setSemanticThreshold(v[0] ?? 0.75)}
                  disabled={!localSearchAvailable}
                />
              </div>

              <div className="space-y-2">
                <Label>Top-N similar ({topN})</Label>
                <Slider
                  value={[topN]}
                  min={1}
                  max={5}
                  step={1}
                  onValueChange={(v) => setTopN(v[0] ?? 3)}
                  disabled={!localSearchAvailable}
                />
              </div>
            </div>

            <Separator />

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setBuildSignal((x) => x + 1)}>
                Build graph
              </Button>
              <Button variant="outline" onClick={() => setClearSignal((x) => x + 1)}>
                Clear
              </Button>
              <Button
                variant={guardEnabled ? 'secondary' : 'default'}
                onClick={() => {
                  if (guardEnabled) {
                    disableAgentGuard();
                    toast({ title: 'Agent guard disabled' });
                  } else {
                    enableAgentGuard();
                    toast({ title: 'Agent guard enabled' });
                  }
                }}
              >
                <Shield className="h-4 w-4 mr-2" />
                Guard: {guardEnabled ? 'ON' : 'OFF'}
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  clearAllowlistPrefixes();
                  toast({ title: 'Allowlist cleared' });
                }}
              >
                Clear allowlist
              </Button>
              <div className="ml-auto text-sm text-muted-foreground flex items-center">
                Allowlist: {allowlistSummary}
              </div>
            </div>
          </CardContent>
        </Card>

        <FileGraph
          scope={scope}
          showContainment={showContainment}
          showSemantic={showSemantic}
          showActivity={showActivity}
          semanticThreshold={semanticThreshold}
          topN={topN}
          buildSignal={buildSignal}
          clearSignal={clearSignal}
          onAllowlistApplied={(prefixes) => {
            toast({
              title: 'Allowlist applied',
              description: prefixes.length ? `${prefixes.length} prefix(es) set` : 'No prefixes selected',
            });
          }}
        />
      </div>
    </MainLayout>
  );
}